/**
 * Color scheme hook — replacement for MUI's useColorScheme().
 *
 * Reads and sets the color scheme via a CSS class on <html>.
 * Uses zustand with persist middleware for state management.
 */

import { useEffect } from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type ColorScheme = 'light' | 'dark'
export type ColorSchemeMode = 'light' | 'dark' | 'system'

const getSystemScheme = (): ColorScheme => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light'
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

const resolveScheme = (mode: ColorSchemeMode): ColorScheme => (mode === 'system' ? getSystemScheme() : mode)

const applyColorScheme = (scheme: ColorScheme) => {
  if (typeof document === 'undefined') {
    return
  }
  document.documentElement.classList.remove('light', 'dark')
  document.documentElement.classList.add(scheme)
}

interface ColorSchemeState {
  mode: ColorSchemeMode
  colorScheme: ColorScheme
  setMode: (mode: ColorSchemeMode) => void
  setColorScheme: (scheme: ColorScheme) => void
}

const useColorSchemeStore = create(
  persist<ColorSchemeState>(
    set => ({
      mode: 'system',
      colorScheme: getSystemScheme(),
      setMode: (mode: ColorSchemeMode) => {
        set({ mode, colorScheme: resolveScheme(mode) })
      },
      setColorScheme: (scheme: ColorScheme) => {
        set({ mode: scheme, colorScheme: scheme })
      },
    }),
    {
      name: 'tuna-color-scheme',
      // Only persist mode — colorScheme is always derived from mode + system preference
      partialize: state => ({ mode: state.mode }) as unknown as ColorSchemeState,
      merge: (persisted, current) => {
        const mode = (persisted as Partial<ColorSchemeState>)?.mode ?? current.mode
        return { ...current, mode, colorScheme: resolveScheme(mode) }
      },
      migrate: persisted => {
        if (persisted && typeof persisted === 'object') {
          return persisted as ColorSchemeState
        }
        try {
          const legacy = localStorage.getItem('mui-mode') as ColorSchemeMode | null
          if (legacy) {
            return { mode: legacy, colorScheme: resolveScheme(legacy) } as ColorSchemeState
          }
        } catch {
          /* noop */
        }
        return { mode: 'system', colorScheme: getSystemScheme() } as ColorSchemeState
      },
      version: 1,
    }
  )
)

// Apply initial color scheme to DOM before first render
applyColorScheme(useColorSchemeStore.getState().colorScheme)

/**
 * Hook for reading and setting the color scheme.
 *
 * Drop-in replacement for MUI's `useColorScheme()`.
 * Returns `{ colorScheme, setColorScheme, mode, setMode }`.
 */
export const useColorScheme = () => {
  const colorScheme = useColorSchemeStore(s => s.colorScheme)
  const mode = useColorSchemeStore(s => s.mode)
  const setMode = useColorSchemeStore(s => s.setMode)
  const setColorScheme = useColorSchemeStore(s => s.setColorScheme)

  // Sync the DOM class when colorScheme changes
  useEffect(() => {
    applyColorScheme(colorScheme)
  }, [colorScheme])

  // Listen for OS theme changes when in system mode
  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    const mql = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => {
      if (useColorSchemeStore.getState().mode === 'system') {
        useColorSchemeStore.setState({ colorScheme: getSystemScheme() })
      }
    }
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  return { colorScheme, setColorScheme, mode, setMode }
}
